// The instance detail shell: header, lifecycle controls and tab navigation.
// Migrated onto components/ui — the tab strip is deliberately NOT converted;
// see the note above the <nav> below.

import { useState } from 'react'
import { useParams, useNavigate, Routes, Route, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInstance, useLifecycle, useCloneInstance } from '../hooks/useInstances'
import StatusBadge from '../components/StatusBadge'
import { MailTab } from './MailTab'
import { mailEnabled } from '../config/edition'
import { useAuthStore } from '../store/authStore'
import { isBusy } from '../lib/instance-status'
import { extractApiError, useStatusMessageText } from './instance/shared'
import { OverviewTab } from './instance/OverviewTab'
import { DomainsTab } from './instance/DomainsTab'
import { LogsTab } from './instance/LogsTab'
import { BackupsTab } from './instance/BackupsTab'
import { ConsoleTab } from './instance/ConsoleTab'
import { AdminTab } from './instance/AdminTab'
import { DangerTab } from './instance/DangerTab'
import { OnboardingSpotlight } from './instance/OnboardingSpotlight'
import { AddonsTab } from './instance/addons/AddonsTab'
import {
  Alert, Button, Card, CardHeader, Field, Input, LinkButton, PageHeader, Skeleton,
} from '../components/ui'

export default function InstanceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const lifecycle = useLifecycle()
  const cloneMutation = useCloneInstance()
  const [showClone, setShowClone] = useState(false)
  const [cloneName, setCloneName] = useState('')
  const [cloneSubdomain, setCloneSubdomain] = useState('')
  const [cloneError, setCloneError] = useState('')
  const { t } = useTranslation()
  const formatStatusMessage = useStatusMessageText()

  const { data: instance, isLoading, error } = useInstance(id!)

  const rawPlan = useAuthStore((s) => s.plan)
  const role = useAuthStore((s) => s.role)
  const plan = rawPlan ?? 'free'

  const [lifecycleError, setLifecycleError] = useState('')

  // Every lifecycle action goes through this: `mutate` + onError keeps the
  // rejection inside mutation state (no floating promise) and always surfaces
  // the message. None of these carry a confirmation today and none is added.
  const runLifecycle = (m: { mutate: (id: string, opts: { onError: (e: unknown) => void }) => void }) => {
    if (!instance) return
    setLifecycleError('')
    m.mutate(instance.id, {
      onError: (e: unknown) => setLifecycleError(extractApiError(e) ?? t('detail.lifecycleError')),
    })
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8 space-y-2">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    )
  }

  if (error || !instance) {
    return (
      <div className="p-4 sm:p-8">
        <Alert tone="danger">{t('detail.notFound')}</Alert>
      </div>
    )
  }

  const TABS = [
    { path: '',        label: t('detail.tabs.overview') },
    { path: 'addons',  label: t('detail.tabs.addons')   },
    { path: 'domains', label: t('detail.tabs.domains')  },
    { path: 'backups', label: t('detail.tabs.backups') },
    { path: 'logs',    label: t('detail.tabs.logs')     },
    ...(mailEnabled ? [{ path: 'mail', label: t('detail.tabs.mail') }] : []),
    ...(plan !== 'free' || role === 'admin'
      ? [{ path: 'console', label: t('detail.tabs.console') }] : []),
    ...(role === 'admin'
      ? [{ path: 'admin', label: t('detail.tabs.admin') }] : []),
    { path: 'danger',  label: t('detail.tabs.danger')   },
  ]

  const baseUrl = `/instances/${id}`
  const busy = isBusy(instance.status)

  return (
    <div className="p-4 sm:p-8">
      {busy && (
        <Alert tone="warning" className="mb-4 sticky top-2 z-20 shadow-sm" title={t('detail.busyBanner')}>
          {instance.status_message && (
            <div className="text-xs">{formatStatusMessage(instance.status_message)}</div>
          )}
          <div className="mt-2 h-2 w-full rounded-full bg-amber-200 overflow-hidden">
            <div className="h-full w-1/3 bg-amber-500 animate-pulse" />
          </div>
        </Alert>
      )}

      <LinkButton tone="neutral" onClick={() => navigate('/dashboard')} className="mb-1 block">
        {t('detail.backToAll')}
      </LinkButton>

      <PageHeader
        title={instance.name}
        // Spans, not divs: PageHeader renders `description` inside a <p>, and a
        // block-level child there is invalid nesting (React warns about it).
        description={
          <>
            <span className="flex items-center gap-3">
              <StatusBadge status={instance.status} />
              {instance.status_message && busy && (
                <span className="text-xs text-gray-500">
                  {formatStatusMessage(instance.status_message)}
                </span>
              )}
            </span>
            {busy && (
              <span className="mt-2 block w-48 sm:w-64 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <span className="block h-full bg-brand-500 rounded-full animate-pulse w-1/2" />
              </span>
            )}
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!busy && instance.status === 'running' && (
              <>
                {/* Stays an <a>: it opens the tenant's Odoo in a new tab, and
                    Button renders a <button>. */}
                <a href={instance.custom_url || instance.subdomain_url} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  {t('detail.open')}
                </a>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => runLifecycle(lifecycle.restart)}
                  loading={lifecycle.restart.isPending}
                >
                  {t('detail.restart')}
                </Button>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => runLifecycle(lifecycle.stop)}
                  loading={lifecycle.stop.isPending}
                >
                  {t('detail.stop')}
                </Button>
                {(plan !== 'free' || role === 'admin') && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setCloneName(instance.name + ' (Staging)')
                      setCloneSubdomain(instance.subdomain + '-staging')
                      setCloneError('')
                      setShowClone(true)
                    }}
                  >
                    {t('detail.clone.button')}
                  </Button>
                )}
              </>
            )}
            {!busy && instance.status === 'stopped' && (
              <Button size="sm" onClick={() => runLifecycle(lifecycle.start)} loading={lifecycle.start.isPending}>
                {t('detail.start')}
              </Button>
            )}
            {!busy && instance.status === 'error' && (
              <>
                {instance.status_message && (
                  <span className="text-xs text-red-600 max-w-52 truncate" title={instance.status_message}>
                    {t('detail.lastOperationFailed', {
                      defaultValue: 'Last operation failed: {{message}}',
                      message: instance.status_message,
                    })}
                  </span>
                )}
                {instance.odoo_container_id ? (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => runLifecycle(lifecycle.start)}
                    loading={lifecycle.start.isPending}
                  >
                    {t('detail.start')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => runLifecycle(lifecycle.retry)}
                    loading={lifecycle.retry.isPending}
                  >
                    {t('detail.retry')}
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {lifecycleError && <Alert tone="danger" className="mb-4">{lifecycleError}</Alert>}

      {/* Inline panel, not a dialog — it has always pushed the page down rather
          than covering it, so `Modal` would change the interaction. */}
      {showClone && (
        <Card className="mb-4">
          <CardHeader title={t('detail.clone.title')} description={t('detail.clone.description')} className="mb-4" />
          {cloneError && <Alert tone="danger" className="mb-3">{cloneError}</Alert>}
          <div className="space-y-3">
            <Field label={t('detail.clone.name')}>
              {(props) => (
                <Input {...props} type="text" value={cloneName} onChange={(e) => setCloneName(e.target.value)} />
              )}
            </Field>
            <Field label={t('detail.clone.subdomain')}>
              {(props) => (
                <Input
                  {...props}
                  type="text"
                  className="font-mono"
                  value={cloneSubdomain}
                  onChange={(e) => setCloneSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
              )}
            </Field>
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setShowClone(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1"
                onClick={async () => {
                  setCloneError('')
                  try {
                    const result = await cloneMutation.mutateAsync({ id: instance.id, name: cloneName, subdomain: cloneSubdomain })
                    setShowClone(false)
                    navigate(`/instances/${result.id}`)
                  } catch (e: unknown) {
                    setCloneError(extractApiError(e) ?? t('detail.cloneExtra.failed'))
                  }
                }}
                disabled={!cloneName.trim() || !cloneSubdomain.trim()}
                loading={cloneMutation.isPending}
              >
                {t('detail.clone.submit')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <OnboardingSpotlight
        instance={instance}
        onGoToAddons={() => navigate(`${baseUrl}/addons`)}
        onGoToBackups={() => navigate(`${baseUrl}/backups`)}
      />

      {/* Navigation, not controls: these are NavLinks driving the <Routes> below.
          The border-b-2 underline bar is its own idiom - SegmentedControl (the
          white-chip-on-grey toggle) is a different one, and swapping it here
          would be a redesign, not a migration. */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex gap-0 -mb-px min-w-max">
          {TABS.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path === '' ? baseUrl : `${baseUrl}/${tab.path}`}
              end={tab.path === ''}
              className={({ isActive }) =>
                `px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-brand-500 text-brand-600'
                    : tab.path === 'danger'
                    ? 'border-transparent text-red-400 hover:text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Routes>
        <Route index element={<OverviewTab instance={instance} />} />
        <Route path="addons" element={<AddonsTab instance={instance} />} />
        <Route path="domains" element={<DomainsTab instance={instance} />} />
        <Route path="backups" element={<BackupsTab instance={instance} />} />
        <Route path="logs" element={<LogsTab instance={instance} />} />
        {mailEnabled && <Route path="mail" element={<MailTab instance={instance} />} />}
        <Route path="console" element={<ConsoleTab instance={instance} />} />
        {role === 'admin' && <Route path="admin" element={<AdminTab instance={instance} />} />}
        <Route path="danger" element={<DangerTab instance={instance} />} />
      </Routes>
    </div>
  )
}
